import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://last-cot.onrender.com/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('adminUser');
      // Only redirect to login if we're on an admin route
      if (window.location.pathname.startsWith('/admin')) {
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  changePassword: (passwordData) => api.put('/auth/change-password', passwordData),
};

// Create a separate axios instance for public requests (no auth interceptors)
const publicApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Products API
export const productsAPI = {
  // Public endpoints (no authentication required)
  getAll: (params) => publicApi.get('/products/public', { params }),
  getById: (id) => publicApi.get(`/products/public/${id}`),
  getPublic: (params) => publicApi.get('/products/public', { params }),
  
  // Admin endpoints (authentication required)
  getAllAdmin: (params) => api.get('/products', { params }),
  getByIdAdmin: (id) => api.get(`/products/${id}`),
  create: (formData) => api.post('/products', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (id, formData) => api.put(`/products/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  delete: (id) => api.delete(`/products/${id}`),
  getStats: () => api.get('/products/stats/overview'),
  toggleStatus: (id) => api.put(`/products/${id}/toggle-status`),
};

// Brands API
export const brandsAPI = {
  getAll: (params) => api.get('/brands', { params }),
  getById: (id) => api.get(`/brands/${id}`),
  create: (data) => api.post('/brands', data),
  update: (id, data) => api.put(`/brands/${id}`, data),
  delete: (id) => api.delete(`/brands/${id}`),
  getAllSimple: () => api.get('/brands/all'),
};

// Equipment Types API
export const equipmentTypesAPI = {
  getAll: (params) => api.get('/equipment-types', { params }),
  getById: (id) => api.get(`/equipment-types/${id}`),
  create: (data) => api.post('/equipment-types', data),
  update: (id, data) => api.put(`/equipment-types/${id}`, data),
  delete: (id) => api.delete(`/equipment-types/${id}`),
  getAllSimple: () => api.get('/equipment-types/all'),
};

// Manufacturers API
export const manufacturersAPI = {
  getAll: (params) => api.get('/manufacturers', { params }),
  getById: (id) => api.get(`/manufacturers/${id}`),
  create: (data) => api.post('/manufacturers', data),
  update: (id, data) => api.put(`/manufacturers/${id}`, data),
  delete: (id) => api.delete(`/manufacturers/${id}`),
  getAllSimple: () => api.get('/manufacturers/all'),
};

// Part Categories API
export const partCategoriesAPI = {
  getAll: (params) => api.get('/part-categories', { params }),
  getById: (id) => api.get(`/part-categories/${id}`),
  create: (data) => api.post('/part-categories', data),
  update: (id, data) => api.put(`/part-categories/${id}`, data),
  delete: (id) => api.delete(`/part-categories/${id}`),
  getAllSimple: () => api.get('/part-categories/all'),
};

// Legacy Categories API (for backward compatibility)
export const categoriesAPI = {
  // Public endpoints (no authentication required)
  getAll: () => publicApi.get('/products/public/categories/all'),
  
  // Admin endpoints (authentication required)
  getAllAdmin: () => api.get('/products/categories/all'),
  add: (type, value) => api.post('/products/categories/add', { type, value }),
  update: (type, oldValue, newValue) => api.put('/products/categories/update', { type, oldValue, newValue }),
  delete: (type, value) => api.delete('/products/categories/delete', { data: { type, value } }),
  
  // Individual category management
  getPartCategories: () => api.get('/products/categories/part-categories'),
  getManufacturers: () => api.get('/products/categories/manufacturers'),
  getEquipmentTypes: () => api.get('/products/categories/equipment-types'),
  getBrands: () => api.get('/products/categories/brands'),
};

// Content API
export const contentAPI = {
  getAll: () => api.get('/content'),
  getBySection: (section) => api.get(`/content/${section}`),
  update: (section, data) => api.put(`/content/${section}`, data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  initialize: (section) => api.post(`/content/${section}/initialize`),
  delete: (section) => api.delete(`/content/${section}`),
  getPublic: (section) => api.get(`/content/public/${section}`),
};

// Contact Requests API
export const contactRequestsAPI = {
  getAll: () => api.get('/contact-requests'),
  getById: (id) => api.get(`/contact-requests/${id}`),
  markAsRead: (id) => api.put(`/contact-requests/${id}/read`),
  markAsReplied: (id) => api.put(`/contact-requests/${id}/replied`),
  delete: (id) => api.delete(`/contact-requests/${id}`),
  submit: (data) => api.post('/contact-requests', data),
};

// Admin API
export const adminAPI = {
  getUsers: (params) => api.get('/admin/users', { params }),
  getUserById: (id) => api.get(`/admin/users/${id}`),
  updateUser: (id, userData) => api.put(`/admin/users/${id}`, userData),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  toggleUserStatus: (id) => api.put(`/admin/users/${id}/toggle-status`),
  getStats: () => api.get('/admin/stats'),
};

export default api;